import { defaultSiteConfig, type SiteConfig } from '@/mocks/siteConfigData';
import * as api from '@/utils/apiClient';

const STORAGE_KEY = 'admin_site_config';

// ---- memory cache ----
let memoryCache: SiteConfig | null = null;

function getCache(): SiteConfig | null {
  return memoryCache;
}

function setCache(config: SiteConfig) {
  memoryCache = config;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // storage full or disabled — ignore
  }
}

// ---- localStorage helpers ----

function loadFromLocalStorage(): SiteConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return deepMerge({ ...defaultSiteConfig }, parsed);
  } catch {
    return null;
  }
}

function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const result = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const sv = source[key];
    const tv = target[key];
    if (sv !== undefined && sv !== null) {
      if (typeof sv === 'object' && !Array.isArray(sv) && typeof tv === 'object' && !Array.isArray(tv) && tv !== null) {
        result[key] = deepMerge(tv as Record<string, unknown>, sv as Record<string, unknown>) as T[keyof T];
      } else {
        result[key] = sv as T[keyof T];
      }
    }
  }
  return result;
}

// ---- API helpers ----

async function fetchFromRemote(): Promise<SiteConfig | null> {
  try {
    const config = await api.fetchSiteConfig();
    if (!config || Object.keys(config).length === 0) return null;
    return deepMerge({ ...defaultSiteConfig }, config as Partial<SiteConfig>);
  } catch {
    return null;
  }
}

async function upsertToRemote(config: SiteConfig): Promise<boolean> {
  try {
    await api.saveSiteConfig(config as unknown as Record<string, unknown>);
    return true;
  } catch {
    return false;
  }
}

async function deleteFromRemote(): Promise<boolean> {
  try {
    await api.resetSiteConfig();
    return true;
  } catch {
    return false;
  }
}

// ---- public API ----

/**
 * Synchronous read — returns memory cache if available, otherwise
 * falls back to localStorage, then defaults.
 */
export function getSiteConfig(): SiteConfig {
  const cached = getCache();
  if (cached) return cached;

  const local = loadFromLocalStorage();
  if (local) {
    setCache(local);
    return local;
  }

  const defaults = { ...defaultSiteConfig };
  setCache(defaults);
  return defaults;
}

/**
 * Called once at app startup. Fetches remote config from API,
 * seeds it if localStorage has data that remote doesn't,
 * and populates the memory cache.
 */
export async function initSiteConfig(): Promise<void> {
  // Try remote first
  const remote = await fetchFromRemote();

  if (remote) {
    setCache(remote);
    return;
  }

  // Remote is empty — try seeding from localStorage
  const local = loadFromLocalStorage();
  if (local) {
    setCache(local);
    // Seed remote with localStorage data so it survives publish
    await upsertToRemote(local);
    return;
  }

  // Nothing anywhere — seed remote and cache with defaults
  const defaults = { ...defaultSiteConfig };
  setCache(defaults);
  await upsertToRemote(defaults);
}

/**
 * Saves config to both remote and localStorage cache.
 * Returns true if remote write succeeded.
 */
export async function saveSiteConfig(config: SiteConfig): Promise<boolean> {
  setCache(config);
  return upsertToRemote(config);
}

/**
 * Resets to defaults — clears remote row and localStorage,
 * then re-seeds with defaults.
 */
export async function resetSiteConfig(): Promise<SiteConfig> {
  await deleteFromRemote();
  localStorage.removeItem(STORAGE_KEY);

  const defaults = { ...defaultSiteConfig };
  setCache(defaults);
  await upsertToRemote(defaults);
  return defaults;
}
